import { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { fetchFastApi } from "../../api/fastapi";
import { consumePostLoginRedirect } from "../../lib/authRedirect";
import { resetOptroAutoSignInAttempt } from "../../lib/optroReferrer";
import { consumeOptroStateBinding } from "../../lib/optroStateBinding";
import { LoadingSpinner } from "../../components/ui/loadingSpinner/loadingSpinner";
import { useAuth } from "../../contexts/authContext.tsx";
import { useCheckTokenAndFetchUser } from "../../hooks/useCheckTokenAndFetchUser.ts";

/**
 * Callback landing for the "Sign in with Optro" flow. Mirrors SSOCallback
 * (WorkOS): Optro redirects the browser here with an authorization `code`, and
 * we hand that code to the backend to exchange for a Midship session.
 *
 * The backend `/auth/optrooauth/callback` returns a WorkOSCallbackResponse-shaped
 * payload with camelCase tokens (`idToken`/`accessToken`/`refreshToken` — the
 * shared `Model` base applies a to_camel alias generator), matching every other
 * auth path in the app (email, Google, refresh-tokens).
 */
export const OptroCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const { setAuth } = useAuth();
  const { checkAndRefreshToken, fetchUser } = useCheckTokenAndFetchUser();
  const hasRun = useRef(false);

  useEffect(() => {
    // Guard against React StrictMode's double-invoke: the auth code + state are
    // single-use, so a second run would race and fail on already-consumed state.
    if (hasRun.current) return;
    hasRun.current = true;
    const handleCallback = async () => {
      try {
        const code = searchParams.get("code");
        const state = searchParams.get("state");
        const providerError = searchParams.get("error");
        const errorDescription = searchParams.get("error_description");

        // Handle an error returned by Optro on the redirect.
        if (providerError) {
          setError(
            errorDescription ||
              providerError ||
              "An error occurred during Optro authentication",
          );
          setLoading(false);
          return;
        }

        if (!code) {
          setError("Missing authorization code from Optro");
          setLoading(false);
          return;
        }

        // Exchange the code for a Midship session via the backend. The binding
        // nonce proves this is the browser that started the flow — the backend
        // rejects the state without it (see lib/optroStateBinding).
        const stateBinding = consumeOptroStateBinding();
        const response = await fetchFastApi({
          method: "GET",
          path: `/auth/optrooauth/callback?code=${encodeURIComponent(code)}${state ? `&state=${encodeURIComponent(state)}` : ""}${stateBinding ? `&state_binding=${encodeURIComponent(stateBinding)}` : ""}`,
        });

        if (response.error || response.error_message) {
          setError(
            response.error_message || response.error || "Authentication failed",
          );
          setLoading(false);
          return;
        }

        if (response.accessToken && response.refreshToken && response.idToken) {
          // Reuse the same Bearer/localStorage session storage as every other
          // auth path (SignIn email/Google, SSOCallback, refetchAccessToken).
          // BE returns WorkOS-style camelCase tokens (model_dump by_alias).
          localStorage.setItem("access_token", response.accessToken);
          localStorage.setItem("refresh_token", response.refreshToken);
          localStorage.setItem("id_token", response.idToken);

          const accessToken = await checkAndRefreshToken();
          if (accessToken) {
            const {
              parsedUser,
              parsedWorkspace,
              parsedWorkspaceMember,
              parsedWorkspaces,
              parsedAnalyticsDestination,
              parsedAnalyticsIdentity,
            } = await fetchUser();

            if (parsedUser && parsedWorkspace && parsedWorkspaceMember) {
              setAuth({
                user: parsedUser,
                loading: false,
                accessToken,
                workspace: parsedWorkspace,
                workspaceMember: parsedWorkspaceMember,
                workspaces: parsedWorkspaces,
                analyticsDestination: parsedAnalyticsDestination,
                analyticsIdentity: parsedAnalyticsIdentity,
              });
            } else if (parsedUser) {
              setAuth({
                user: parsedUser,
                loading: false,
                accessToken,
                workspace: null,
                workspaceMember: null,
                workspaces: parsedWorkspaces,
                analyticsDestination: parsedAnalyticsDestination,
                analyticsIdentity: parsedAnalyticsIdentity,
              });
            } else {
              setAuth({
                user: null,
                loading: false,
                accessToken: null,
                workspace: null,
                workspaceMember: null,
                workspaces: [],
                analyticsDestination: null,
                analyticsIdentity: null,
              });
            }

            resetOptroAutoSignInAttempt();
            // Redirect to the originally requested URL, else the default app.
            navigate(consumePostLoginRedirect("/audit"), { replace: true });
          } else {
            setError("Something went wrong in handling callback");
            setLoading(false);
          }
        } else {
          setError("Invalid response from authentication server");
          setLoading(false);
        }
      } catch (err) {
        console.error("Optro callback error:", err);
        setError("An unexpected error occurred during authentication");
        setLoading(false);
      }
    };

    handleCallback();
  }, [searchParams, navigate, checkAndRefreshToken, fetchUser, setAuth]);

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="flex justify-center mb-4">
            <LoadingSpinner />
          </div>
          <h2 className="mt-4 text-lg font-semibold text-gray-900">
            Completing sign in...
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            Please wait while we finish authenticating you.
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50">
        <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-6">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
              <svg
                className="h-6 w-6 text-red-600"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="1.5"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
                />
              </svg>
            </div>
            <h3 className="mt-4 text-lg font-medium text-gray-900">
              Authentication Failed
            </h3>
            <p className="mt-2 text-sm text-gray-600">{error}</p>
            <div className="mt-6">
              <button
                onClick={() => navigate("/signin", { replace: true })}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Return to Sign In
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
};
